<i18n
  src="@/locales/components/dashboard/agro_exporter/container_export_status.yml"
  lang="yaml"
></i18n>
<template>
  <div class="bg-white shadow rounded-lg p-4 sm:p-6 xl:p-8">
    <template v-if="load_general_data">
      <div class="mb-4 flex justify-between">
        <div>
          <h3 class="text-xl font-bold text-gray-900 mb-2">
            {{ $t("labels.title") }}
          </h3>
          <span class="text-base font-normal text-gray-500">
            {{ $t("labels.description") }}
          </span>
        </div>
        <div class="flex-shrink-0">
          <router-link
            class="text-sm font-medium text-[#5c7a2e] hover:text-[#496320] cursor-pointer hover:bg-slate-100 rounded-lg p-2"
            :to="{
              name: routeName(routePermission, 'ae_production'),
              params: {
                agro_exporter_id: agro_exporter_id
              }
            }"
          >
            {{ $t("labels.show_link") }}
          </router-link>
        </div>
      </div>
      <div class="flex flex-col mt-6">
        <div id="chart">
          <apexchart
            type="bar"
            width="100%"
            height="300"
            :options="chartOptions"
            :series="series"
          ></apexchart>
        </div>
      </div>
      <div class="flex flex-col mt-4">
        <div class="overflow-x-auto rounded-lg">
          <div class="align-middle inline-block min-w-full">
            <div class="shadow overflow-hidden sm:rounded-lg">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.agro_exporter_process") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.total_containers") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.in_preparation") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.in_transit") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.arrived") }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white table-body">
                  <tr
                    v-for="(containers, index) in containers_export_status"
                    :key="index"
                    class="table-row"
                  >
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ containers.agro_exporter_process }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ containers.total_containers }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ containers.in_preparation }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ containers.in_transit }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ containers.arrived }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import Requester from "@/components/views/dashboard/requesters/agro_exporter/container_export.js";

export default {
  name: "DashboardAEContainerExportStatus",
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      containers_export_status: [],
      chartOptions: {
        chart: {
          type: "bar",
          stacked: true
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              total: {
                enabled: true,
                style: {
                  fontSize: "14px",
                  fontWeight: 900
                }
              },
              value: {
                style: {
                  fontSize: "16px"
                }
              }
            },
            barHeight: "40%"
          }
        },
        stroke: {
          width: 1,
          colors: ["#fff"]
        },
        yaxis: {
          title: {
            text: undefined
          },
          labels: {
            style: {
              fontSize: "14px"
            },
          }
        },
        xaxis: {
          categories: []
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          offsetX: 40,
          fontSize: "14px"
        },
        colors: ["#eab308", "#3b82f6", "#65a30d"],
        dataLabels: {
          style: {
            fontSize: "14px"
          }
        },
      },

      load_general_data: false
    };
  },
  props: {
    agro_exporter_id: { required: true }
  },
  created() {
    this.fetchSummary();
  },
  computed: {
    routePermission() {
      if (
        this.checkIfUserIsAuthorized(
          "production",
          "basic_container_export",
          "agro_exporter"
        )
      )
        return "basic_container_export";
      else if (
        this.checkIfUserIsAuthorized(
          "production",
          "query_container_export",
          "agro_exporter"
        )
      )
        return "query_container_export";
      else return "";
    },
    series() {
      return [
        {
          name: this.$t("labels.in_preparation"),
          data: this.containers_export_status.map(item => item.in_preparation)
        },
        {
          name: this.$t("labels.in_transit"),
          data: this.containers_export_status.map(item => item.in_transit)
        },
        {
          name: this.$t("labels.arrived"),
          data: this.containers_export_status.map(item => item.arrived)
        }
      ];
    }
  },
  methods: {
    async fetchSummary() {
      const response = await Requester.fetchSummary(this.agro_exporter_id);
      this.containers_export_status = response.data;
      this.chartOptions.xaxis.categories = this.containers_export_status.map(
        container =>
          this.divideAgroExporterProcess(container.agro_exporter_process)
      );
      if (this.containers_export_status.length == 1)
        this.chartOptions.plotOptions.bar.barHeight = "20%";
      this.load_general_data = true;
    },
    divideAgroExporterProcess(text) {
      let text_len = text.split(" ").length;
      return [
        text
          .split(" ")
          .slice(0, text_len / 2)
          .join(" "),
        text
          .split(" ")
          .slice(text_len / 2, text_len)
          .join(" ")
      ];
    }
  }
};
</script>
